// src/components/ui/cards/ComponentListItem/ComponentListItem.jsx
import React, { useState } from 'react';
import {
  Edit3,
  Trash2,
  DollarSign,
  Clock,
  Save,
  X,
  Package
} from 'lucide-react';
import { cn } from '../../../../utils/cn';

const ComponentListItem = ({
  component,
  onEdit,
  onDelete,
  isEditing = false,
  onSave,
  onCancelEdit,
  saving = false
}) => {
  const [editData, setEditData] = useState({
    name: component.name || '',
    description: component.description || '',
    price: component.price || '',
    duration_minutes: component.duration_minutes || ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Handle edit data changes
  const handleEditChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate edit data
  const validateEditData = () => {
    const errors = {};

    if (!editData.name?.trim()) {
      errors.name = 'Add-on name is required';
    }

    if (editData.price && (isNaN(editData.price) || parseFloat(editData.price) < 0)) {
      errors.price = 'Price must be a valid positive number';
    }

    if (editData.duration_minutes && (isNaN(editData.duration_minutes) || parseInt(editData.duration_minutes) < 0)) {
      errors.duration_minutes = 'Duration must be 0 or more minutes';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = () => {
    if (!validateEditData()) return;

    const updateData = {
      name: editData.name.trim(),
      description: editData.description?.trim() || null,
      price: editData.price ? parseFloat(editData.price) : null,
      duration_minutes: editData.duration_minutes ? parseInt(editData.duration_minutes) : 0,
      component_type: 'addon'
    };

    onSave(updateData);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditData({
      name: component.name || '',
      description: component.description || '',
      price: component.price || '',
      duration_minutes: component.duration_minutes || ''
    });
    setValidationErrors({});
    onCancelEdit();
  };


  if (isEditing) {
    return (
      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
        <div className="space-y-4">
          {/* Component Name */}
          <div>
            <input
              type="text"
              value={editData.name}
              onChange={(e) => handleEditChange('name', e.target.value)}
              placeholder="Component name"
              className={cn(
                "w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg",
                "text-slate-200 placeholder-slate-400",
                "focus:outline-none focus:border-slate-500/50 focus:ring-2 focus:ring-slate-500/20",
                validationErrors.name && "border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20"
              )}
            />
            {validationErrors.name && (
              <p className="text-red-400 text-sm mt-1">{validationErrors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <textarea
              value={editData.description}
              onChange={(e) => handleEditChange('description', e.target.value)}
              placeholder="Component description (optional)"
              rows={2}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-400 focus:outline-none focus:border-slate-500/50 focus:ring-2 focus:ring-slate-500/20 resize-none"
            />
          </div>

          {/* Price and Duration Row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Price */}
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Price ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={editData.price}
                onChange={(e) => handleEditChange('price', e.target.value)}
                placeholder="0.00"
                className={cn(
                  "w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg",
                  "text-slate-200 placeholder-slate-400",
                  "focus:outline-none focus:border-slate-500/50 focus:ring-2 focus:ring-slate-500/20",
                  validationErrors.price && "border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20"
                )}
              />
              {validationErrors.price && (
                <p className="text-red-400 text-sm mt-1">{validationErrors.price}</p>
              )}
            </div>

            {/* Duration */}
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Duration (min)</label>
              <input
                type="number"
                min="0"
                value={editData.duration_minutes}
                onChange={(e) => handleEditChange('duration_minutes', e.target.value)}
                placeholder="0"
                className={cn(
                  "w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg",
                  "text-slate-200 placeholder-slate-400",
                  "focus:outline-none focus:border-slate-500/50 focus:ring-2 focus:ring-slate-500/20",
                  validationErrors.duration_minutes && "border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20"
                )}
              />
              {validationErrors.duration_minutes && (
                <p className="text-red-400 text-sm mt-1">{validationErrors.duration_minutes}</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={handleCancelEdit}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-2 text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !editData.name?.trim()}
              className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group p-4 bg-slate-700/30 border border-slate-600/30 rounded-xl hover:bg-slate-700/50 hover:border-slate-500/50 transition-all duration-200">
      {/* Add-on Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <h4 className="font-semibold text-slate-200 truncate">
              {component.name}
            </h4>
          </div>

          {component.description && (
            <p className="text-slate-400 text-sm line-clamp-2 ml-7">
              {component.description}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-3">
          <button
            onClick={() => onEdit(component)}
            className="p-1.5 hover:bg-purple-500/20 rounded-lg transition-colors text-slate-400 hover:text-purple-400"
            title="Edit component"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors text-slate-400 hover:text-red-400"
            title="Delete component"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Add-on Details */}
      <div className="flex items-center justify-between ml-7">
        <div className="flex items-center gap-4 text-sm">
          {/* Price */}
          <div className="flex items-center gap-1 text-slate-300">
            <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
            <span className="font-medium">
              +{component.price ? `$${parseFloat(component.price).toFixed(2)}` : '$0.00'}
            </span>
          </div>

          {/* Duration */}
          {component.duration_minutes > 0 && (
            <div className="flex items-center gap-1 text-slate-300">
              <Clock className="w-3.5 h-3.5 text-blue-400" />
              <span className="font-medium">
                +{component.duration_minutes} min
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComponentListItem;